import type { InspectionStatusId, PhotoCategory } from '@areapif/areapif-shared'
import { InspectionStepId, isSealEvidenceStep, stepIdToPhotoCategory } from '@areapif/areapif-shared'

/**
 * The statuses the mobile app drops from its inspections list, and therefore
 * the statuses whose inspection nobody can capture evidence on from the app.
 * Mirrors the `$inspections` query of areapif-app's legend-sync, status for
 * status: the backoffice offers uploads on exactly the inspections the app
 * would, so "ongoing" means the same thing in both places.
 *
 * Keyed on the raw status rather than the backoffice's coarse state on purpose:
 * a PIF inspection at LLEGADA AL FINGER without evidence reads PLANNED here, yet
 * the app lists it and lets the operator capture on it.
 */
const UPLOAD_CLOSED_STATUSES: ReadonlySet<InspectionStatusId> = new Set<InspectionStatusId>([
  'FINALIZADO',
  'ANULADO',
  'CREADO',
  'ASIGNADO A FINGER',
  'EN EL PARKING',
])

export const acceptsEvidenceUploads = (statusId: InspectionStatusId): boolean => !UPLOAD_CLOSED_STATUSES.has(statusId)

/**
 * The largest image the capture edge functions accept, in decoded bytes
 * (`MAX_IMAGE_BYTES` in areapif-backend's `_shared/image.ts`). Checked before
 * the request is sent, so an oversized image is refused without a round trip.
 */
export const CAPTURE_MAX_BYTES = 10 * 1024 * 1024

/** Every image is sent as a JPEG, as the app sends it; the key extension follows. */
export const CAPTURE_CONTENT_TYPE = 'image/jpeg'

const SHORT_ID_CHARS = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'

/** The app's `shortId`: the random suffix that makes each capture key unique. */
export const shortId = (length = 6, random: () => number = Math.random): string =>
  Array.from({ length }, () => SHORT_ID_CHARS.charAt(Math.floor(random() * SHORT_ID_CHARS.length))).join('')

/**
 * The backend confines a capture key's file name to `[A-Za-z0-9._-]`. A
 * container number is already made of those; anything else is replaced rather
 * than sent to a certain rejection.
 */
const safeSegment = (value: string): string => value.replace(/[^A-Za-z0-9._-]+/g, '_')

/**
 * The storage key of a capture, which is also the id of its row: the app's
 * `getInspectionPhotoPath` / `getInspectionSealPath` with the Spanish nouns the
 * app writes, so a backoffice capture cannot be told apart from an app capture
 * by its key.
 */
const captureKey = (noun: 'foto' | 'precinto', inspectionId: string, container: string, suffix: string): string =>
  `inspections/${inspectionId}/${safeSegment(container)}-${noun}-${suffix}.jpg`

export interface PhotoCaptureRequest {
  functionName: 'persist-inspection-photo'
  body: {
    photoId: string
    inspectionId: string
    category: PhotoCategory
    imageBase64: string
    contentType: typeof CAPTURE_CONTENT_TYPE
  }
}

export interface SealCaptureRequest {
  functionName: 'process-seal-ocr'
  body: {
    sealId: string
    inspectionId: string
    stepId: InspectionStepId
    imageBase64: string
    contentType: typeof CAPTURE_CONTENT_TYPE
  }
}

export type CaptureRequest = PhotoCaptureRequest | SealCaptureRequest

export interface CaptureTarget {
  inspectionId: string
  container: string
  stepId: InspectionStepId
}

/**
 * The edge function call that records one image on one step, routed the way
 * the app routes a capture: a seal step goes through OCR, which assigns the
 * seal's category and serial number; every other step persists a photo of the
 * step's category.
 */
export const buildCaptureRequest = (target: CaptureTarget, imageBase64: string, suffix: string = shortId()): CaptureRequest => {
  const { inspectionId, container, stepId } = target

  if (isSealEvidenceStep(stepId)) {
    return {
      functionName: 'process-seal-ocr',
      body: {
        sealId: captureKey('precinto', inspectionId, container, suffix),
        inspectionId,
        stepId,
        imageBase64,
        contentType: CAPTURE_CONTENT_TYPE,
      },
    }
  }

  return {
    functionName: 'persist-inspection-photo',
    body: {
      photoId: captureKey('foto', inspectionId, container, suffix),
      inspectionId,
      category: stepIdToPhotoCategory(stepId),
      imageBase64,
      contentType: CAPTURE_CONTENT_TYPE,
    },
  }
}
